import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { PlusIcon, MagnifyingGlassIcon, DocumentTextIcon } from '@heroicons/react/24/outline';
import { format, parseISO } from 'date-fns';
import { getNotes } from '../services/noteService';
import { getClients } from '../services/clientService';
import Badge from '../components/common/Badge';
import EmptyState from '../components/common/EmptyState';
import TableSkeleton from '../components/common/skeletons/TableSkeleton';

const NOTE_TYPE_OPTIONS = [
  { value: '', label: 'All types' },
  // Session Notes
  { value: 'progress',            label: 'Standard Progress Note' },
  { value: 'soap',                label: 'SOAP Note' },
  { value: 'dap',                 label: 'DAP Note' },
  // Assessment & Treatment
  { value: 'intake',              label: 'Intake Note' },
  { value: 'initial_assessment',  label: 'Initial Clinical Assessment' },
  { value: 'assessment',          label: 'Assessment (General)' },
  { value: 'treatment_plan',      label: 'Treatment Plan & Goals' },
  { value: 'behavioral_tx_plan',  label: 'Behavioral Health Treatment Plan' },
  { value: 'couples_tx_plan',     label: 'Couples BH Treatment Plan' },
  // Other Documents
  { value: 'discharge',           label: 'Discharge Summary' },
  { value: 'good_faith_estimate', label: 'Good Faith Estimate' },
];

const NOTE_TYPE_VARIANTS = {
  soap: 'info',
  dap: 'info',
  progress: 'success',
  treatment_plan: 'warning',
  behavioral_tx_plan: 'warning',
  couples_tx_plan: 'warning',
  assessment: 'default',
  initial_assessment: 'default',
  intake: 'default',
  discharge: 'default',
  good_faith_estimate: 'default',
};

export default function Notes() {
  const [clientId, setClientId] = useState('');
  const [noteType, setNoteType] = useState('');
  const [page, setPage] = useState(1);

  const { data: notesData, isLoading, isError } = useQuery({
    queryKey: ['notes', { clientId, noteType, page }],
    queryFn: () =>
      getNotes({ clientId: clientId || undefined, noteType: noteType || undefined, page, limit: 25 }),
    keepPreviousData: true,
  });

  const { data: clientsData } = useQuery({
    queryKey: ['clients', { limit: 200 }],
    queryFn: () => getClients({ limit: 200 }),
  });

  const notes = notesData?.notes ?? [];
  const pagination = notesData?.pagination;
  const clients = clientsData?.clients ?? [];

  const handleClientChange = (e) => { setClientId(e.target.value); setPage(1); };
  const handleTypeChange = (e) => { setNoteType(e.target.value); setPage(1); };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold text-neutral-900 dark:text-neutral-100">Notes</h1>
        <Link to="/notes/new" className="btn btn-primary flex items-center gap-2">
          <PlusIcon className="h-4 w-4" />
          New note
        </Link>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-3 mb-6">
        <div className="relative flex-1 sm:max-w-xs">
          <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-neutral-400 dark:text-neutral-500" />
          <select value={clientId} onChange={handleClientChange} className="input pl-9 w-full">
            <option value="">All clients</option>
            {clients.map((c) => (
              <option key={c.id} value={c.id}>
                {c.lastName}, {c.firstName}
              </option>
            ))}
          </select>
        </div>
        <select value={noteType} onChange={handleTypeChange} className="input w-full sm:w-44">
          {NOTE_TYPE_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>{o.label}</option>
          ))}
        </select>
      </div>

      {/* Content */}
      {isLoading ? (
        <TableSkeleton rows={5} />
      ) : isError ? (
        <div className="text-center py-20 text-danger-600 dark:text-danger-400">Failed to load notes.</div>
      ) : notes.length === 0 ? (
        <EmptyState
          icon={DocumentTextIcon}
          title={clientId || noteType ? 'No notes match your filters' : 'No notes yet'}
          description="Create a note to document a session."
          action={
            !clientId && !noteType && (
              <Link to="/notes/new" className="btn btn-primary">New note</Link>
            )
          }
        />
      ) : (
        <>
          <div className="space-y-3">
            {notes.map((note) => (
              <Link
                key={note.id}
                to={`/notes/${note.id}`}
                className="card flex items-start gap-4 hover:border-neutral-300 transition-colors cursor-pointer block"
              >
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="font-medium text-neutral-900 dark:text-neutral-100 text-sm">
                      {note.client
                        ? `${note.client.firstName} ${note.client.lastName}`
                        : 'Unknown client'}
                    </span>
                    {note.noteType && (
                      <Badge
                        label={note.noteType.replace('_', ' ')}
                        variant={NOTE_TYPE_VARIANTS[note.noteType] || 'default'}
                      />
                    )}
                    {note.isSigned && <Badge label="Signed" variant="success" />}
                  </div>
                  {note.title && (
                    <p className="text-sm text-neutral-700 dark:text-neutral-300 font-medium mb-1">{note.title}</p>
                  )}
                  <p className="text-sm text-neutral-500 dark:text-neutral-400 truncate">
                    {note.content.slice(0, 120)}{note.content.length > 120 ? '…' : ''}
                  </p>
                </div>
                <div className="text-xs text-neutral-400 dark:text-neutral-500 flex-shrink-0 mt-0.5">
                  {format(parseISO(note.createdAt), 'MMM d, yyyy')}
                </div>
              </Link>
            ))}
          </div>

          {pagination && pagination.pages > 1 && (
            <div className="flex items-center justify-between mt-4 text-sm text-neutral-600 dark:text-neutral-400">
              <span>
                {(pagination.page - 1) * pagination.limit + 1}–
                {Math.min(pagination.page * pagination.limit, pagination.total)} of {pagination.total}
              </span>
              <div className="flex gap-2">
                <button
                  onClick={() => setPage((p) => p - 1)}
                  disabled={page === 1}
                  className="btn btn-secondary disabled:opacity-40"
                >
                  Previous
                </button>
                <button
                  onClick={() => setPage((p) => p + 1)}
                  disabled={page === pagination.pages}
                  className="btn btn-secondary disabled:opacity-40"
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
